import {
  Button as MantineButton,
  ButtonProps as MantineButtonProps,
  MantineSize,
  MantineStyleProp,
  PolymorphicComponentProps,
  Tooltip,
  TooltipProps,
} from "@mantine/core"
import React, { ReactElement, forwardRef } from "react"

import { getIconSize } from "../../utils/icon.utils"

import classes from "./Button.module.css"

export const buttonVariants = [
  "primary",
  "primary-critical",
  "secondary",
  "secondary-critical",
  "tertiary",
  "tertiary-critical",
  "admin",
  "admin-tertiary",
  "info",
  "secondary",
  "input-variant",
  "ghost",
] as const

export type ButtonVariants = (typeof buttonVariants)[number]

export type MantineButtonPropsOmitted = Omit<
  MantineButtonProps,
  "leftSection" | "icon" | "variant" | "style"
>
export type ButtonProps = {
  variant?: ButtonVariants
  children?: React.ReactNode
  size?:
    | MantineSize
    | "compact-xs"
    | "compact-sm"
    | "compact-md"
    | "compact-lg"
    | "compact-xl"
    | (string & NonNullable<unknown>)
    | undefined
  disabled?: boolean
  icon?: ReactElement<any>
  fullWidth?: boolean
  loading?: boolean
  rightSection?: React.ReactNode
  toolTip?: string
  toolTipProps?: Omit<TooltipProps, "label" | "children">
} & MantineButtonPropsOmitted &
  React.ButtonHTMLAttributes<HTMLButtonElement> &
  Omit<PolymorphicComponentProps<"a", MantineButtonPropsOmitted>, "leftSection">

function hasStyleProp(
  element: ReactElement
): element is ReactElement<{ style: MantineStyleProp }> {
  return element.props && "style" in element.props
}

const Button = forwardRef<HTMLAnchorElement, ButtonProps>(
  ({ children, toolTip, toolTipProps, ...props }, ref) => {
    const { icon, size } = props
    const iconSize = getIconSize(size)

    let iconWithSize = icon

    if (icon && React.isValidElement(icon) && hasStyleProp(icon)) {
      iconWithSize = React.cloneElement(icon, {
        style: { ...icon.props.style, height: iconSize, width: iconSize }, // Properly typing style
      })
    }

    const buttonElement = (
      <MantineButton
        classNames={classes}
        ref={ref}
        leftSection={iconWithSize}
        {...props}
      >
        {children}
      </MantineButton>
    )
    return toolTip ? (
      <Tooltip label={toolTip} {...toolTipProps}>
        {buttonElement}
      </Tooltip>
    ) : (
      buttonElement
    )
  }
)

export { Button }
